import { supabase, supabaseUrl } from '@/lib/supabase';
import { isLightViewActionLike } from '@/lib/light-view-action';
import type {
  ActivityFeedSubjectChatRequest,
  ActivityFeedSubjectChatResponse,
} from '@shared/types/activity-feed';

const activityFeedFunctionUrl = `${supabaseUrl}/functions/v1/activity-feed`;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const isSubjectChatResponseLike = (value: unknown): value is ActivityFeedSubjectChatResponse => (
  isRecord(value)
  && (value.kind === 'answer' || value.kind === 'tool_handoff')
  && typeof value.reply === 'string'
  && (value.proposedAction === null || isLightViewActionLike(value.proposedAction))
);

const parseSseEvent = (rawEvent: string): { event: string; data: unknown } | null => {
  const lines = rawEvent
    .split('\n')
    .map((line) => line.trimEnd())
    .filter((line) => line.length > 0);

  if (lines.length === 0) return null;

  let event = 'message';
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
      continue;
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trim());
    }
  }

  if (dataLines.length === 0) return null;

  try {
    return { event, data: JSON.parse(dataLines.join('\n')) as unknown };
  } catch {
    return null;
  }
};

const toStreamingError = async (response: Response): Promise<Error> => {
  const bodyText = await response.text();
  try {
    const parsed = JSON.parse(bodyText) as { error?: string; message?: string };
    const message = parsed.message ?? parsed.error;
    if (typeof message === 'string' && message.length > 0) return new Error(message);
  } catch {
    // Ignore JSON parse failures.
  }
  return new Error(bodyText || 'Le streaming de l’assistant a échoué.');
};

/**
 * Stream a subject-chat reply over SSE. The prose streams token-by-token via
 * `onReplyDelta`; the final validated structured response (including any
 * proposed tool handoff) arrives in the returned promise.
 */
export const streamSubjectChat = async (
  request: Omit<ActivityFeedSubjectChatRequest, 'action'>,
  handlers: { onReplyDelta?: (delta: string) => void } = {},
): Promise<ActivityFeedSubjectChatResponse> => {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) {
    throw new Error('Session Supabase manquante pour le streaming de l’assistant.');
  }

  const response = await fetch(activityFeedFunctionUrl, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      accept: 'text/event-stream',
      apikey: supabaseAnonKey,
      authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ action: 'subject_chat', ...request }),
  });

  if (!response.ok) {
    throw await toStreamingError(response);
  }
  if (!response.body) {
    throw new Error('Réponse de streaming invalide de l’assistant.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let completedResponse: ActivityFeedSubjectChatResponse | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    while (true) {
      const separatorIndex = buffer.indexOf('\n\n');
      if (separatorIndex === -1) break;

      const rawEvent = buffer.slice(0, separatorIndex);
      buffer = buffer.slice(separatorIndex + 2);

      const parsedEvent = parseSseEvent(rawEvent);
      if (!parsedEvent) continue;

      if (parsedEvent.event === 'reply_chunk') {
        const data = parsedEvent.data as { text?: unknown };
        if (typeof data.text === 'string' && data.text.length > 0) {
          handlers.onReplyDelta?.(data.text);
        }
        continue;
      }

      if (parsedEvent.event === 'complete') {
        if (!isSubjectChatResponseLike(parsedEvent.data)) {
          throw new Error('Réponse finale invalide de l’assistant.');
        }
        completedResponse = parsedEvent.data;
        continue;
      }

      if (parsedEvent.event === 'error') {
        const data = parsedEvent.data as { message?: unknown };
        throw new Error(
          typeof data.message === 'string' ? data.message : 'Le streaming de l’assistant a échoué.',
        );
      }
    }
  }

  if (!completedResponse) {
    throw new Error('Le streaming de l’assistant s’est terminé sans réponse finale.');
  }

  return completedResponse;
};
